import { componentText, HostAriaLabelMixin, SchmancyFormField } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { createRef, ref } from 'lit/directives/ref.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import { concatMap, Subject, takeUntil, tap } from 'rxjs'
import { acceptRefusal, acceptsFile, type FileRefusal } from '../dropzone/accept'
import '@schmancy/primitives/button/button'
import '@schmancy/primitives/button/icon-button'
import '@schmancy/primitives/icons/icon'
import '@schmancy/primitives/media-frame/media-frame'
import '@schmancy/primitives/typography/typography'

/**
 * One attached image, already downscaled and re-encoded by the control.
 */
export interface SchmancyAttachment {
	/** The picked file's own name — the thumbnail's alt text and the remove control's accessible name. */
	name: string
	/** MIME type of the ENCODED image (`image/jpeg` for everything this control produces), not of the file the user picked. */
	type: string
	/** `data:image/jpeg;base64,…` — the entire payload. */
	dataUrl: string
	/** `dataUrl.length`. All-ASCII, so this is also its UTF-8 byte cost — the exact number `maxBytes` caps. */
	bytes: number
	/** Pixel width of the ENCODED raster, after the downscale ladder — not of the file the user picked. */
	width: number
	/** Pixel height of the ENCODED raster. With `width`, the shape of the thing photographed, which is what a preview has to honour. */
	height: number
}

export type SchmancyAttachmentsChangeEvent = CustomEvent<{ value: SchmancyAttachment[] }>

type EncodeOutcome =
	| { readonly ok: true; readonly attachment: SchmancyAttachment }
	| { readonly ok: false; readonly refusal: FileRefusal }

/**
 * @element schmancy-attachments
 *
 * Image attachment field: paste (⌘V), drop, or pick a file, and the control
 * hands back a `data:` URI per attachment.
 *
 * The byte ceiling is the contract, not an optimization. A consumer writing
 * the result into a document with a hard total-size limit (a Firestore
 * document is 1 MB, shared with everything else that document already
 * carries) cannot accept whatever the camera produced, so every image is
 * downscaled and re-encoded here until its data URI fits `maxBytes` — and
 * when it cannot fit, the file is refused out loud rather than silently
 * blowing the document limit at write time.
 *
 * `change` fires on user interaction only — adding or removing an
 * attachment. A programmatic `.value` set never echoes, so a consumer needs
 * no CONTROLLED_VALUE_ECHO_IS_NOT_INTENT guard.
 *
 * @fires change - `{ value: SchmancyAttachment[] }`
 * @fires refusal - `{ file, cause, message }` — one per file turned away, the
 *   same event `<schmancy-dropzone>` fires from the same `accept` seam. This
 *   control decodes what it takes, so it reaches a cause the dropzone never
 *   can: a HEIC passes `image/*` on family and fails at the decoder, arriving
 *   as `unreadable` rather than `type`.
 */
@customElement('schmancy-attachments')
export class SchmancyAttachments extends HostAriaLabelMixin(SchmancyFormField(css`
	:host {
		display: block;
	}
`)) {
	// FACE wiring (formAssociated, internals, attachInternals) plus name,
	// label, required, disabled, readonly, error, validationMessage, hint,
	// validateOn, touched/dirty/pristine/submitted, markTouched/markSubmitted,
	// formResetCallback, formDisabledCallback, FIELD_CONNECT_EVENT dispatch,
	// emitChange(), reportValidity(), setCustomValidity() all come from
	// SchmancyFormField.

	/** Forwarded to the native file input's `accept`, and applied to dropped and pasted files too. Narrows within images; a non-image is refused whatever this says, because the encode step needs a decodable image. */
	@property({ type: String }) accept = 'image/*'

	/** How many attachments the field will hold. */
	@property({ type: Number }) max = 1

	/**
	 * `dropzone` (default) is the dashed target with the instruction inside it.
	 * `button` is a compact text button carrying `label` as its own visible
	 * text — for an action row with no room for the zone; the same picker, and
	 * drop and paste still land on the field around it, handing back the same
	 * records.
	 */
	@property({ type: String, reflect: true }) variant: 'dropzone' | 'button' = 'dropzone'

	/**
	 * The glyph on the trigger — the button's prefix, the zone's mark.
	 * `add_photo_alternate` reads "attach a picture you have"; a camera-first
	 * surface passes `add_a_photo`, which reads "take one".
	 */
	@property({ type: String }) icon = 'add_photo_alternate'

	/** Byte ceiling for EACH encoded data URI. The encode loop steps encoder fidelity, then dimensions, until the result fits; a file that still cannot fit is refused. */
	@property({ type: Number, attribute: 'max-bytes' }) maxBytes = 600_000

	/** Longest-edge cap (px) the encode loop starts from. */
	@property({ type: Number, attribute: 'max-dimension' }) maxDimension = 1600

	/**
	 * The array instance the caller passes in is stored as-is: the mixin
	 * tracks `dirty` by comparing `value` to the default it captured by
	 * reference, and every internal edit here replaces the array wholesale,
	 * so preserving the caller's instance is what lets `form.reset()` land
	 * back on pristine instead of leaving a required field painting its
	 * error the moment it is reset.
	 */
	@property({ attribute: false })
	override get value(): SchmancyAttachment[] {
		return this.items
	}
	override set value(next: SchmancyAttachment[]) {
		this.items = Array.isArray(next) ? next : []
	}

	@state() private items: SchmancyAttachment[] = []
	@state() private dragging = false
	/** Transient refusal — a non-image, a file over the ceiling, one too many. Distinct from `validationMessage`, which is the field's own validity. */
	@state() private notice = ''

	private readonly fileInput = createRef<HTMLInputElement>()
	private readonly picked$ = new Subject<readonly File[]>()

	override connectedCallback(): void {
		super.connectedCallback()

		this.picked$
			.pipe(
				// Serial, because the inners share one mutable list and the
				// order files were dropped in is the order they should appear.
				// The index is the file's place in ITS OWN gesture, which is
				// what the single-value rule below is stated in.
				concatMap(files => files.map((file, inGesture) => ({ file, inGesture }))),
				concatMap(async ({ file, inGesture }): Promise<EncodeOutcome> => {
					// A single-value field means the value is replaceable: the
					// first image of a gesture takes the place of whatever is
					// held, which is the whole difference between one slot and
					// one slot that can only be filled once. The rest of that
					// same gesture have no defensible destination — exactly the
					// "which one gets dropped" that keeps max > 1 refusing at
					// full — so they are turned away by name rather than
					// silently overwriting each other.
					const noRoom = this.max === 1 ? inGesture > 0 : this.items.length >= this.max
					if (noRoom) {
						return {
							ok: false,
							refusal: {
								file,
								cause: 'tooMany',
								message:
									this.max === 1
										? componentText('attachments.refusal.oneAtATime', { name: file.name })
										: `Only ${this.max} attachments fit here.`,
							},
						}
					}

					const type = file.type.toLowerCase()
					if (!type.startsWith('image/')) {
						return { ok: false, refusal: { file, cause: 'type', message: `${file.name} is not an image.` } }
					}

					if (!acceptsFile(this.accept, file)) {
						return { ok: false, refusal: { file, cause: 'type', message: acceptRefusal(this.accept, file) } }
					}

					// Everything that can throw stays inside the try: this pipe is
					// long-lived, and anything escaping here would end the
					// subscription for good, taking every later attachment with it.
					let objectUrl = ''
					try {
						objectUrl = URL.createObjectURL(file)
						const image = new Image()
						image.src = objectUrl
						await image.decode()

						const canvas = document.createElement('canvas')
						const context = canvas.getContext('2d')
						// Not a fact about the file, but from the app's side it is the same
						// answer: nothing here could be read into an attachment.
						if (!context)
							return {
								ok: false,
								refusal: { file, cause: 'unreadable', message: 'This browser cannot encode images.' },
							}

						// Two knobs, spent in the order that costs the least
						// visible detail: encoder fidelity first down to a
						// floor, then the longest edge, then fidelity again
						// from a mid value. Both ladders run to their floor
						// before the file is refused — an attempt cap here
						// would end the search while it still had room, and
						// refuse an image a smaller edge would have fit.
						// Termination is the ladders: fidelity strictly falls
						// until it is at its floor, then edge does, then break.
						let edge = this.maxDimension
						let fidelity = 0.82
						for (;;) {
							const scale = Math.min(1, edge / Math.max(image.naturalWidth, image.naturalHeight))
							canvas.width = Math.max(1, Math.round(image.naturalWidth * scale))
							canvas.height = Math.max(1, Math.round(image.naturalHeight * scale))
							context.drawImage(image, 0, 0, canvas.width, canvas.height)
							const dataUrl = canvas.toDataURL('image/jpeg', fidelity)
							if (dataUrl.length <= this.maxBytes) {
								return {
									ok: true,
									attachment: {
										name: file.name,
										type: 'image/jpeg',
										dataUrl,
										bytes: dataUrl.length,
										width: canvas.width,
										height: canvas.height,
									},
								}
							}
							if (fidelity > 0.31) fidelity *= 0.75
							else if (edge > 480) {
								edge = Math.round(edge * 0.7)
								fidelity = 0.6
							} else break
						}
						return {
							ok: false,
							refusal: {
								file,
								cause: 'tooLarge',
								message: `${file.name} is too detailed to fit in ${Math.round(this.maxBytes / 1000)} kB — crop it or shrink it first.`,
							},
						}
					} catch {
						// Where a HEIC lands: `image/*` admitted it and no mainstream browser
						// ships a decoder for it.
						return {
							ok: false,
							refusal: { file, cause: 'unreadable', message: `${file.name} could not be read as an image.` },
						}
					} finally {
						if (objectUrl) URL.revokeObjectURL(objectUrl)
					}
				}),
				tap(outcome => {
					// strict:false narrows this union only through an explicit
					// `=== false` — `!outcome.ok` leaves `outcome` unnarrowed.
					if (outcome.ok === false) {
						this.notice = outcome.refusal.message
						this.dispatchScopedEvent('refusal', outcome.refusal, { bubbles: true })
						return
					}
					this.notice = ''
					this.value = this.max === 1 ? [outcome.attachment] : [...this.items, outcome.attachment]
					this.markTouched()
					this.emitChange({ value: this.items })
				}),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	/**
	 * The platform's `setFormValue` takes no arrays, so the submitted value is
	 * a FormData carrying one data URI per attachment under the field's name —
	 * the same shape `toFormEntries()` contributes. The second argument is the
	 * restore state: the full records as JSON, so a bfcache restore comes back
	 * with file names and byte counts intact rather than bare data URIs.
	 */
	protected override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (!changed.has('items') && !changed.has('value') && !changed.has('name')) return

		const submitted = new FormData()
		const restoreState = new FormData()
		for (const attachment of this.items) {
			if (this.name) submitted.append(this.name, attachment.dataUrl)
			restoreState.append('attachment', JSON.stringify(attachment))
		}
		this.internals?.setFormValue(submitted, restoreState)
	}

	/** The mixin's required test is `value === ''`; an empty list is not `''`, so a required field with nothing attached would otherwise pass. */
	override checkValidity(): boolean {
		if (this.required && !this.disabled && this.items.length === 0) {
			return this.setFieldValidity({ valueMissing: true }, this.errorMessages?.valueMissing ?? 'This field is required')
		}
		return super.checkValidity()
	}

	/** One entry per attachment, each carrying that attachment's data URI. */
	override toFormEntries(): Array<[string, FormDataEntryValue]> {
		if (!this.name || this.disabled) return []
		return this.items.map(attachment => [this.name, attachment.dataUrl] as [string, FormDataEntryValue])
	}

	/** The mixin assigns the restore state straight to `value`; this field's is a FormData of JSON records, not an array. */
	override formStateRestoreCallback(restoreState: string | File | FormData | null): void {
		if (!(restoreState instanceof FormData)) return
		try {
			this.value = restoreState.getAll('attachment').map(entry => JSON.parse(String(entry)) as SchmancyAttachment)
		} catch {
			this.value = []
		}
	}

	override render() {
		const full = this.items.length >= this.max
		const locked = this.disabled || this.readonly
		// A single-value field is never out of room: the next pick takes the
		// place of the one held. Only max > 1 reaches a state where the field
		// can take nothing more, because only there does dropping one to make
		// room have no default answer.
		const noRoom = full && this.max > 1
		const help = noRoom
			? `All ${this.max} attachments are in place.`
			: full
				? 'Drop, paste, or choose another image to replace this one.'
				: 'Drop an image, paste it, or click to choose a file.'
		const labelClasses = {
			'block mb-1 font-medium text-sm': true,
			'opacity-40': this.disabled,
			// The treatment `<schmancy-input>` and `<schmancy-textarea>` give
			// their own labels, to the utility: two fields in one panel are
			// peers or one of them reads as an afterthought under the other.
			'text-surface-onVariant': !this.error,
			'text-error-default': this.error,
		}

		return html`
			<div
				class="grid gap-2"
				@dragover=${(e: DragEvent) => {
					if (locked || noRoom) return
					e.preventDefault()
					this.dragging = true
				}}
				@dragleave=${(e: DragEvent) => {
					const leftFor = e.relatedTarget
					if (leftFor instanceof Node && (e.currentTarget as Node).contains(leftFor)) return
					this.dragging = false
				}}
				@drop=${(e: DragEvent) => {
					this.dragging = false
					if (locked) return
					const dropped = Array.from(e.dataTransfer?.files ?? [])
					if (dropped.length === 0) return
					e.preventDefault()
					this.picked$.next(dropped)
				}}
				@paste=${(e: ClipboardEvent) => {
					if (locked) return
					const pasted = Array.from(e.clipboardData?.items ?? [])
						.filter(item => item.kind === 'file')
						.map(item => item.getAsFile())
						.filter(file => file !== null)
					if (pasted.length === 0) return
					e.preventDefault()
					this.picked$.next(pasted)
				}}
			>
				${when(
					!!this.label && this.variant !== 'button',
					// The button variant carries `label` as the trigger's own
					// visible text, so a second line of it here would say the
					// same thing twice in two typographic voices.
					() => html`<label class=${this.classMap(labelClasses)}>${this.label}</label>`,
				)}
				${when(
					this.items.length > 0,
					() => html`
						<ul
							class=${this.classMap({
								// A grid item's automatic minimum size refuses to shrink below its
								// content, and a percentage max-width against an `auto` track is
								// circular and ignored while that track is sized — so a grid here
								// hands the held record's own definite width (960px for a 2400×600
								// panorama under the 240px height cap) straight past every
								// `max-w-full` below it, painting 960px wide out of a 352px field.
								// Only max > 1 has tiles to lay out; one record needs no track and
								// no gap, and block layout is where `max-w-full` resolves.
								// `min-w-0` because this list is itself a grid item of the field's
								// own wrapper, where the same minimum would overflow the field first.
								'm-0 min-w-0 list-none p-0': true,
								'grid grid-cols-3 gap-2': this.max > 1,
							})}
						>
							${repeat(
								this.items,
								attachment => attachment.dataUrl,
								attachment => {
									// A record whose geometry did not survive — restore state
									// written before this field recorded any, a hand-built
									// record from untyped code — has no shape to lay out at,
									// and a frame handed one paints nothing at all. The tile
									// is the honest floor: smaller, but on screen.
									const framed =
										this.max === 1 &&
										Number.isFinite(attachment.width) &&
										Number.isFinite(attachment.height) &&
										attachment.width > 0 &&
										attachment.height > 0
									return html`
										<li
											class=${this.classMap({
												relative: true,
												// The one held record sizes its own cell, so the
												// remove control lands on the picture's corner
												// rather than at the far end of an empty row.
												'w-max max-w-full': framed,
												'overflow-hidden rounded-lg bg-surface-containerHigh': !framed,
											})}
										>
											${when(
												framed,
												// The only question a single attachment exists to
												// answer is what shape the thing is — a can, not a
												// bottle — and a centre-crop into a fixed box is
												// precisely the crop that removes it. So the held
												// record is laid out from its own geometry inside a
												// height cap: whole picture, its own aspect, never
												// upscaled. Widening it would not help; `cover` into
												// a wider, equally short box shows LESS of the
												// subject, not more.
												() => html`
													<schmancy-media-frame
														class="max-w-full rounded-lg"
														.box=${{ width: attachment.width, height: attachment.height }}
														max-height="240"
														src=${attachment.dataUrl}
														alt=${attachment.name}
													></schmancy-media-frame>
												`,
												// Several held records are a set to scan, and a
												// uniform grid is what makes them scannable; each
												// tile is a handle on its record, not the answer.
												() => html`
													<img
														src=${attachment.dataUrl}
														alt=${attachment.name}
														class="block h-24 w-full object-cover"
													/>
												`,
											)}
											${when(
												!locked,
												() => html`
													<schmancy-icon-button
														class="absolute top-1 right-1"
														size="xs"
														variant="filled"
														icon="close"
														aria-label=${componentText('chip.remove', { name: attachment.name })}
														@click=${() => {
															this.notice = ''
															this.value = this.items.filter(item => item !== attachment)
															this.markTouched()
															this.emitChange({ value: this.items })
														}}
													></schmancy-icon-button>
												`,
											)}
										</li>
									`
								},
							)}
						</ul>
					`,
				)}
				${when(
					this.variant === 'button',
					// This trigger carries NO description, and the absence is the
					// decision. The node holding the button role is one shadow root
					// deeper and an IDREF does not reach it (`button.ts` forwards none,
					// deliberately) — but forwarding is not what is missing: there is
					// nothing here worth announcing. A screen reader already says
					// "button", so `help`'s "click to choose a file" is the control's own
					// semantics restated; and its "drop" and "paste" name the field
					// AROUND this button, which in the button form has no visible or
					// focusable locus, so announcing them sends a reader hunting for a
					// target that cannot be navigated to. Folding `help` into the name
					// instead would survive WCAG 2.5.3 on prefix match, but exact
					// visible-text == accessible-name is what makes voice control
					// activate this trigger verbatim, and that is worth more than a
					// tautology plus an unreachable target. The paste gesture is real and
					// field-level: its honest home is the consumer's `hint`, which renders
					// in every variant and claims to belong to no one control.
					//
					// Latent, and unreached today (no consumer pairs `button` with
					// max > 1): there `noRoom` disables this trigger and nothing says
					// why. The repair is a reason node in THIS shadow root — the
					// precedent `<schmancy-list-item>` sets — or the `notice` line below,
					// already `role="alert"` and already rendered in both variants. Not
					// an `aria-describedby` from here, which is what was tried and what
					// reached no one.
					() => html`
						<schmancy-button
							.ariaLabel=${this.ariaLabel}
							variant="text"
							size="sm"
							class=${this.classMap({
								'justify-self-start': true,
								'rounded-full outline-2 outline-primary-default': this.dragging,
							})}
							?disabled=${locked || noRoom}
							@click=${() => this.fileInput.value?.click()}
						>
							<schmancy-icon slot="prefix">${this.icon}</schmancy-icon>
							${this.label || componentText('attachments.attach')}
						</schmancy-button>
					`,
					() => html`
						<button
							type="button"
							aria-label=${this.ariaLabel || this.label || componentText('attachments.attach')}
							aria-describedby="attachments-help"
							?disabled=${locked || noRoom}
							class=${this.classMap({
								'grid w-full justify-items-center gap-1 rounded-xl border-2 border-dashed px-6 py-6 text-center transition-colors': true,
								'border-control-border bg-surface-container': !this.dragging && !this.error,
								'border-primary-default bg-primary-container': this.dragging,
								'border-error-default': this.error && !this.dragging,
								'outline-2 outline-error-default': this.error,
								'hover:border-primary-default focus-visible:outline-2 focus-visible:outline-primary-default':
									!locked && !noRoom,
								'opacity-40': locked || noRoom,
							})}
							@click=${() => this.fileInput.value?.click()}
						>
							<schmancy-icon class="text-surface-onVariant" aria-hidden="true">${this.icon}</schmancy-icon>
							<schmancy-typography id="attachments-help" type="body" token="sm" class="text-surface-onVariant">
								${help}
							</schmancy-typography>
						</button>
					`,
				)}
				${when(!!this.notice, () => html`<div role="alert" class="text-sm text-error-default">${this.notice}</div>`)}
				${when(
					this.error && !!this.validationMessage,
					() => html`<div role="alert" class="text-sm wrap-anywhere text-error-default">${this.validationMessage}</div>`,
				)}
				${when(!!this.hint && !this.error, () => html`<div class="text-sm wrap-anywhere text-surface-onVariant">${this.hint}</div>`)}

				<input
					${ref(this.fileInput)}
					type="file"
					class="sr-only"
					tabindex="-1"
					aria-hidden="true"
					accept=${this.accept}
					?multiple=${this.max > 1}
					@change=${(e: Event) => {
						const input = e.target as HTMLInputElement
						const chosen = Array.from(input.files ?? [])
						input.value = ''
						if (chosen.length > 0) this.picked$.next(chosen)
					}}
				/>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-attachments': SchmancyAttachments
	}
}
